import { useEffect, useMemo, useRef, useState } from 'react';
import SpeechButton from './SpeechButton.jsx';
import { checkResponse } from '../lib/api.js';
import {
  acceptedPassageAnswer,
  blankResponses,
  completedPassage,
  GUIDED_MAXIMUM,
  guidedScore,
  passageParts,
} from '../lib/guided.js';
import {
  countWords,
  hasExactlyOneSentence,
  normaliseAnswer,
  replaceBlank,
  sample,
  shuffle,
} from '../lib/utils.js';

function patchAt(setter, index, patch) {
  setter((current) => current.map((state, stateIndex) => (
    stateIndex === index ? { ...state, ...patch } : state
  )));
}

function StepButtons({ current, states, isDone, isSkipped, onSelect, onGenerate, generating, colour }) {
  return (
    <div className="step-controls">
      <div className="step-buttons" aria-label="Round steps">
        {states.map((state, index) => {
          const done = isDone(state);
          const skipped = isSkipped?.(state);
          const active = index === current;
          return (
            <button
              className={`step-button step-${colour} ${done ? 'done' : ''} ${skipped ? 'skipped' : ''} ${active ? 'active' : ''}`}
              type="button"
              key={index}
              disabled={!done && !active}
              onClick={() => onSelect(index)}
              aria-label={`Step ${index + 1}${skipped ? ', passed' : done ? ', completed' : active ? ', current' : ', locked'}`}
            >
              {skipped ? '—' : done ? '✓' : index + 1}
            </button>
          );
        })}
      </div>
      <button className={`ai-round-button ai-${colour}`} type="button" onClick={onGenerate} disabled={generating}>
        ✨ {generating ? 'Generating…' : 'AI-generated round'}
      </button>
    </div>
  );
}

function Completion({ label, score, maximum, onGenerate, generating, colour }) {
  return (
    <div className="inline-completion" role="status">
      <span>✓ {label} completed — {score}/{maximum}</span>
      <button className={`ai-round-button ai-${colour}`} type="button" onClick={onGenerate} disabled={generating}>
        ✨ {generating ? 'Generating…' : 'Try an AI-generated round'}
      </button>
    </div>
  );
}

function GuidedRunner({
  theme,
  headword,
  items,
  passage,
  passageStatus,
  passageError,
  roundType,
  onComplete,
  onGenerate,
  onPreparePassage,
  generating,
}) {
  const questions = useMemo(() => {
    const fixedChoices = items.map((item) => ({
      id: item.id,
      text: item.canonicalForm,
      textZh: item.glossZh,
    }));
    return shuffle(items).map((item) => ({
      item,
      options: shuffle(fixedChoices),
    }));
  }, [items]);
  const [index, setIndex] = useState(0);
  const [states, setStates] = useState(() => questions.map(() => ({
    attempts: 0,
    passed: false,
    mark: false,
    selected: '',
    feedback: '',
  })));
  const [passageState, setPassageState] = useState({
    passageKey: '',
    responses: {},
    correct: {},
    marks: {},
    attempts: 0,
    complete: false,
    skipped: false,
    checking: false,
    feedback: '',
  });
  const reported = useRef(false);
  const passageKey = passage?.id ?? passage?.textWithBlanks ?? '';
  const passageRound = index === 5;
  const question = passageRound ? null : questions[index];
  const state = passageRound ? null : states[index];
  const score = guidedScore(states, passageState.marks);
  const passageFinished = passageState.complete || passageState.skipped;
  const allComplete = states.every((value) => value.passed) && passageFinished;
  const choiceModelAnswer = !passageRound && question
    ? question.item.guidedPractice.collocationChoice.modelAnswer
      ?? question.item.exampleSentence
      ?? question.item.guidedPractice.sentenceUpgrade?.modelAnswer
      ?? {
        text: question.item.guidedPractice.collocationChoice.sentenceWithBlank
          .replace('{{blank}}', question.item.canonicalForm),
        textZh: question.item.glossZh,
      }
    : null;
  const stepStates = [
    ...states,
    { passed: passageState.complete, skipped: passageState.skipped, loading: passageStatus === 'loading' },
  ];

  useEffect(() => {
    if (!passage || !passageKey || passageState.passageKey === passageKey) return;
    setPassageState({
      passageKey,
      responses: blankResponses(passage.blanks),
      correct: {},
      marks: {},
      attempts: 0,
      complete: false,
      skipped: false,
      checking: false,
      feedback: '',
    });
  }, [passage, passageKey, passageState.passageKey]);

  useEffect(() => {
    if (!allComplete || reported.current) return;
    reported.current = true;
    onComplete({
      score,
      maximum: GUIDED_MAXIMUM,
      targetIds: questions.map(({ item }) => item.sourceTargetId ?? item.id),
    });
  }, [allComplete, onComplete, questions, score]);

  function choose(option) {
    if (state.passed) return;
    const correct = option.id === question.item.id;
    patchAt(setStates, index, {
      selected: option.id,
      attempts: state.attempts + 1,
      passed: correct,
      mark: correct && state.attempts === 0,
      feedback: correct
        ? state.attempts === 0
          ? '✅ Correct — 1 mark earned.'
          : '✅ Correct. The target collocation is now revealed.'
        : '❌ Use the sentence context and compare all five collocations before trying again.',
    });
  }

  function updatePassageResponse(blankId, response) {
    setPassageState((current) => ({
      ...current,
      responses: { ...current.responses, [blankId]: response },
      feedback: '',
    }));
  }

  async function checkPassage(event) {
    event.preventDefault();
    if (!passage || passageState.complete || passageState.skipped || passageState.checking) return;
    const localResults = Object.fromEntries(passage.blanks.map((blank) => [
      blank.id,
      passageState.correct[blank.id]
        || acceptedPassageAnswer(passageState.responses[blank.id], blank),
    ]));
    const firstAttempt = passageState.attempts === 0;

    const recordResults = (results, aiVerdicts = []) => {
      const correctCount = Object.values(results).filter(Boolean).length;
      const acceptedAlternatives = aiVerdicts.filter((verdict) => verdict.pass).length;
      const rejected = aiVerdicts.find((verdict) => !verdict.pass);
      const rejectedBlank = rejected
        ? passage.blanks.findIndex((blank) => blank.id === rejected.blank_id) + 1
        : 0;
      const aiNote = acceptedAlternatives
        ? ` AI accepted ${acceptedAlternatives} alternative ${acceptedAlternatives === 1 ? 'answer' : 'answers'}.`
        : '';
      const revisionNote = rejected?.feedback
        ? ` Blank ${rejectedBlank}: ${rejected.feedback}`
        : '';
      setPassageState((current) => ({
        ...current,
        attempts: current.attempts + 1,
        correct: results,
        marks: Object.fromEntries(passage.blanks.map((blank) => [
          blank.id,
          current.marks[blank.id] || (firstAttempt && results[blank.id]),
        ])),
        complete: correctCount === passage.blanks.length,
        checking: false,
        feedback: correctCount === passage.blanks.length
          ? `✅ All five answers are correct${firstAttempt ? ' — 5 marks earned.' : '.'}${aiNote}`
          : `❌ ${correctCount}/5 correct.${aiNote} Keep the correct answers and revise the remaining blanks.${revisionNote}`,
      }));
    };

    const unresolved = passage.blanks.filter((blank) => !localResults[blank.id]);
    if (!unresolved.length) {
      recordResults(localResults);
      return;
    }

    setPassageState((current) => ({
      ...current,
      correct: localResults,
      marks: Object.fromEntries(passage.blanks.map((blank) => [
        blank.id,
        current.marks[blank.id] || (firstAttempt && localResults[blank.id]),
      ])),
      checking: true,
      feedback: '✨ Checking possible alternative forms in context…',
    }));

    try {
      const result = await checkResponse({
        mode: 'guided-passage',
        theme_id: theme.id,
        headword_id: headword.id,
        item_id: passage.id ?? `${headword.id}-guided-passage`,
        passage_title: passage.title,
        passage_text_with_blanks: passage.textWithBlanks,
        answers: unresolved.map((blank) => {
          const target = items.find((item) => item.id === blank.targetId
            || item.sourceTargetId === blank.targetId);
          return {
            blank_id: blank.id,
            blank_token: blank.token,
            target_id: blank.targetId,
            target_collocation: target?.canonicalForm ?? blank.answer,
            accepted_targets: target?.acceptedCanonicalVariants ?? [target?.canonicalForm ?? blank.answer],
            expected_answer: blank.answer,
            accepted_answers: blank.acceptedAnswers ?? [blank.answer],
            student_response: passageState.responses[blank.id],
          };
        }),
      });
      const aiVerdicts = Array.isArray(result.results) ? result.results : [];
      const aiById = new Map(aiVerdicts.map((verdict) => [verdict.blank_id, verdict.pass === true]));
      const results = Object.fromEntries(passage.blanks.map((blank) => [
        blank.id,
        localResults[blank.id] || aiById.get(blank.id) === true,
      ]));
      recordResults(results, aiVerdicts);
    } catch (error) {
      const correctCount = Object.values(localResults).filter(Boolean).length;
      setPassageState((current) => ({
        ...current,
        checking: false,
        correct: localResults,
        feedback: `⚠️ ${correctCount}/5 stored answers were confirmed, but AI could not check the remaining alternatives. Try Check passage again.`,
      }));
    }
  }

  function passPassage() {
    if (passageState.complete || passageState.skipped || passageState.checking) return;
    setPassageState((current) => ({
      ...current,
      skipped: true,
      checking: false,
      feedback: '',
    }));
  }

  return (
    <section className="exercise-mode exercise-guided">
      <div className="exercise-mode-heading">
        <div>
          <p className="eyebrow mode-guided-text">{roundType === 'ai' ? 'GUIDED PRACTICE · AI ROUND' : roundType === 'cached' ? 'GUIDED PRACTICE · VALIDATED FALLBACK' : 'GUIDED PRACTICE'}</p>
          <h2>Use five collocations in context</h2>
          <p>Complete five sentences, then apply all five collocations in one contextualized passage.</p>
        </div>
        <StepButtons
          current={index}
          states={stepStates}
          isDone={(value) => value.passed || value.skipped}
          isSkipped={(value) => value.skipped}
          onSelect={setIndex}
          onGenerate={onGenerate}
          generating={generating}
          colour="guided"
        />
      </div>

      <article className="exercise-card guided-card">
        <div className="exercise-card-header">
          <div>
            <span>ROUND {index + 1} OF 6</span>
            {passageRound ? (
              <strong>Context challenge</strong>
            ) : state.passed ? (
              <div className="revealed-heading">
                <strong>{question.item.canonicalForm}</strong>
                <SpeechButton text={question.item.audio?.speechText ?? question.item.canonicalForm} label={`Play ${question.item.canonicalForm}`} />
                <small lang="zh-Hant">{question.item.glossZh}</small>
              </div>
            ) : (
              <strong className="hidden-target">Choose from the five stored collocations</strong>
            )}
          </div>
          <span className="marks-pill">{passageRound ? '5 blanks · 5 marks' : '1 question · 1 mark'}</span>
        </div>

        <div className="exercise-card-body">
          {!passageRound && (
            <section className="question-block">
              <div className="question-title">
                <span>{index + 1}</span>
                <div><strong>Complete the sentence</strong><small>Choose the one collocation that fits this context.</small></div>
              </div>
              <p className="question-sentence">{replaceBlank(question.item.guidedPractice.collocationChoice.sentenceWithBlank)}</p>
              <div className="guided-five-options">
                {question.options.map((option, optionIndex) => {
                  const selected = state.selected === option.id;
                  const correct = state.passed && option.id === question.item.id;
                  return (
                    <div className="option-row" key={option.id}>
                      <button
                        className={`answer-option ${selected ? 'selected' : ''} ${correct ? 'correct' : ''}`}
                        type="button"
                        disabled={state.passed}
                        onClick={() => choose(option)}
                      >
                        <span>{String.fromCharCode(65 + optionIndex)}</span>
                        <span className="option-copy">
                          <strong>{option.text}</strong>
                          <small lang="zh-Hant">{option.textZh}</small>
                        </span>
                      </button>
                      <SpeechButton text={option.text} label={`Play ${option.text}`} />
                    </div>
                  );
                })}
              </div>
              {state.feedback && (
                <div className={`feedback ${state.passed ? 'feedback-correct' : 'feedback-incorrect'}`}>
                  <p>{state.feedback}</p>
                  {state.passed && choiceModelAnswer && (
                    <div className="guided-answer-review">
                      <div>
                        <strong>{choiceModelAnswer.text}</strong>
                        <small lang="zh-Hant">{choiceModelAnswer.textZh}</small>
                      </div>
                      <SpeechButton
                        text={choiceModelAnswer.audio?.speechText ?? choiceModelAnswer.text}
                        label="Play the completed sentence"
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {passageRound && passageStatus === 'loading' && (
            <section className="passage-wait" aria-live="polite">
              <span className="spinner passage-spinner" aria-hidden="true" />
              <h3>Preparing the context challenge…</h3>
              <p>The five sentence rounds are safely recorded while the passage is generated.</p>
            </section>
          )}

          {passageRound && passageStatus === 'error' && (
            <section className="passage-wait passage-failed" role="alert">
              <h3>Round 6 is not ready yet</h3>
              <p>{passageError || 'The passage could not be generated. Your first five answers are still saved.'}</p>
              <button className="primary-button guided-primary" type="button" onClick={onPreparePassage}>Retry Round 6</button>
            </section>
          )}

          {passageRound && passage && passageStatus === 'ready' && (
            <section className="question-block passage-challenge">
              <div className="question-title">
                <span>6</span>
                <div>
                  <strong>{passage.title}</strong>
                  <small>Use every collocation once. Change its form when the grammar requires it.</small>
                </div>
              </div>
              <div className="collocation-bank" aria-label="Collocation bank">
                {items.map((item) => (
                  <span key={item.id}>
                    <span className="collocation-bank-copy">
                      <b>{item.canonicalForm}</b>
                      <small lang="zh-Hant">{item.glossZh}</small>
                    </span>
                    <SpeechButton text={item.canonicalForm} label={`Play ${item.canonicalForm}`} />
                  </span>
                ))}
              </div>
              <form className="response-form" onSubmit={checkPassage}>
                <div className="passage-text">
                  {passageParts(passage.textWithBlanks).map((part, partIndex) => {
                    if (part.type === 'text') return <span key={`text-${partIndex}`}>{part.text}</span>;
                    const blank = passage.blanks.find((value) => value.token === part.token);
                    if (!blank) return <span key={part.token}>________</span>;
                    return (
                      <label className={`passage-blank ${passageState.correct[blank.id] ? 'correct' : ''}`} key={blank.id}>
                        <span className="sr-only">Blank {part.number}</span>
                        <b aria-hidden="true">{part.number}</b>
                        <input
                          type="text"
                          value={passageState.responses[blank.id] ?? ''}
                          onChange={(event) => updatePassageResponse(blank.id, event.target.value)}
                          disabled={passageState.correct[blank.id] || passageState.skipped || passageState.checking}
                          aria-label={`Answer for blank ${part.number}`}
                          autoComplete="off"
                        />
                      </label>
                    );
                  })}
                </div>
                {!passageFinished && (
                  <div className="response-actions">
                    <button
                      className="primary-button guided-primary"
                      type="submit"
                      disabled={passageState.checking || passage.blanks.some((blank) => !passageState.correct[blank.id] && !passageState.responses[blank.id]?.trim())}
                    >
                      {passageState.checking ? 'Checking alternatives…' : 'Check passage'}
                    </button>
                    <button className="pass-button" type="button" onClick={passPassage} disabled={passageState.checking}>Pass</button>
                  </div>
                )}
              </form>
              {passageState.skipped && <div className="passed-notice">Passed — the remaining blanks were skipped and no answer key was shown.</div>}
              {passageState.feedback && <div className={`feedback ${passageState.checking ? 'feedback-checking' : passageState.complete ? 'feedback-correct' : 'feedback-incorrect'}`}>{passageState.feedback}</div>}
              {passageState.complete && (
                <div className="model-answer passage-model">
                  <strong>Completed passage</strong>
                  <p>{completedPassage(passage)}</p>
                  <p className="support-text passage-translation" lang="zh-Hant">{passage.textZh}</p>
                  <SpeechButton text={completedPassage(passage)} label="Play completed passage" />
                </div>
              )}
            </section>
          )}
        </div>
      </article>

      {!passageRound && state.passed && index < 5 && (
        <button className="primary-button next-button" type="button" onClick={() => setIndex(index + 1)}>{index === 4 ? 'Start context challenge →' : 'Next sentence →'}</button>
      )}
      {allComplete && (
        <Completion label="Guided Practice" score={score} maximum={GUIDED_MAXIMUM} onGenerate={onGenerate} generating={generating} colour="guided" />
      )}
    </section>
  );
}

function SentenceBuildingRunner({ headword, items, roundType, onComplete, onGenerate, generating }) {
  const questions = useMemo(() => shuffle(items), [items]);
  const [index, setIndex] = useState(0);
  const [states, setStates] = useState(() => questions.map(() => ({
    response: '',
    feedback: '',
    passed: false,
    skipped: false,
    checking: false,
  })));
  const reported = useRef(false);
  const state = states[index];
  const item = questions[index];
  const task = item.sentenceBuilding;
  const score = states.filter((value) => value.passed).length * 3;
  const complete = (value) => value.passed || value.skipped;
  const allComplete = states.every(complete);

  useEffect(() => {
    if (!allComplete || reported.current) return;
    reported.current = true;
    onComplete({ score, maximum: questions.length * 3, targetIds: questions.map((value) => value.canonicalForm) });
  }, [allComplete, onComplete, questions, score]);

  async function submit(event) {
    event.preventDefault();
    if (countWords(state.response) <= task.minimumWordsExclusive) {
      patchAt(setStates, index, { feedback: `❌ Write more than ${task.minimumWordsExclusive} words.` });
      return;
    }
    if (!hasExactlyOneSentence(state.response)) {
      patchAt(setStates, index, { feedback: '❌ Submit exactly one sentence.' });
      return;
    }
    patchAt(setStates, index, { checking: true, feedback: '✨ Checking how naturally your sentence connects the ideas…' });
    try {
      const result = await checkResponse({
        mode: 'sentence-building',
        theme_id: headword.id.split('-').slice(0, 2).join('-'),
        headword_id: headword.id,
        item_id: item.id,
        target: item.canonicalForm,
        accepted_targets: item.acceptedCanonicalVariants ?? [item.canonicalForm],
        required_ideas: task.ideaPhrases.map((idea) => idea.text),
        student_response: state.response,
      });
      patchAt(setStates, index, {
        checking: false,
        passed: result.pass === true,
        feedback: `${result.pass ? '✅' : '❌'} ${result.feedback}`,
      });
    } catch (error) {
      patchAt(setStates, index, { checking: false, feedback: `⚠️ ${error.message}` });
    }
  }

  function passSentence() {
    patchAt(setStates, index, {
      response: '',
      feedback: '',
      passed: false,
      skipped: true,
      checking: false,
    });
    if (index < questions.length - 1) setIndex(index + 1);
  }

  return (
    <section className="exercise-mode exercise-building">
      <div className="exercise-mode-heading">
        <div>
          <p className="eyebrow mode-building-text">{roundType === 'ai' ? 'SENTENCE BUILDING · AI ROUND' : roundType === 'cached' ? 'SENTENCE BUILDING · VALIDATED FALLBACK' : 'OPTIONAL CHALLENGE'}</p>
          <h2>Build one purposeful sentence</h2>
          <p>Bind the target collocation with both idea phrases in one sentence of more than ten words.</p>
        </div>
        <StepButtons current={index} states={states} isDone={complete} isSkipped={(value) => value.skipped} onSelect={setIndex} onGenerate={onGenerate} generating={generating} colour="building" />
      </div>

      <article className="exercise-card building-card">
        <div className="exercise-card-header">
          <div>
            <span>SENTENCE {index + 1} OF {questions.length}</span>
            <div className="collocation-audio-heading">
              <strong>{item.canonicalForm}</strong>
              <SpeechButton text={item.audio?.speechText ?? item.canonicalForm} label={`Play ${item.canonicalForm}`} />
            </div>
          </div>
          <span className="marks-pill">Accepted = 3 marks</span>
        </div>
        <div className="exercise-card-body">
          <div className="idea-grid">
            <div className="idea-card target-idea">
              <span>TARGET COLLOCATION</span><strong>{item.canonicalForm}</strong><small lang="zh-Hant">{item.glossZh}</small>
              <SpeechButton text={item.audio?.speechText ?? item.canonicalForm} label={`Play ${item.canonicalForm}`} />
            </div>
            {task.ideaPhrases.map((idea, ideaIndex) => (
              <div className="idea-card" key={idea.text}>
                <span>IDEA {ideaIndex + 1}</span><strong>{idea.text}</strong><small lang="zh-Hant">{idea.textZh}</small>
                <SpeechButton text={idea.audio?.speechText ?? idea.text} label={`Play ${idea.text}`} />
              </div>
            ))}
          </div>
          {!state.skipped && <form className="response-form" onSubmit={submit}>
            <label>
              WRITE ONE SENTENCE
              <textarea rows="5" value={state.response} onChange={(event) => patchAt(setStates, index, { response: event.target.value, feedback: '' })} disabled={state.passed || state.checking} />
            </label>
            <span className="word-count">{countWords(state.response)} words · more than 10 required</span>
            {!state.passed && <div className="response-actions"><button className="primary-button building-primary" type="submit" disabled={!state.response.trim() || state.checking}>{state.checking ? 'Checking…' : 'Check my sentence'}</button><button className="pass-button" type="button" onClick={passSentence} disabled={state.checking}>Pass</button></div>}
          </form>}
          {state.skipped && <div className="passed-notice">Passed — this sentence was skipped and not reviewed.</div>}
          {state.feedback && <div className={`feedback ${state.checking ? 'feedback-checking' : state.passed ? 'feedback-correct' : 'feedback-incorrect'}`}>{state.feedback}</div>}
          {state.passed && (
            <div className="model-answer">
              <strong>Model sentence</strong><p>{task.modelAnswer.text}</p><p className="support-text">{task.modelAnswer.textZh}</p>
              <SpeechButton text={task.modelAnswer.audio?.speechText ?? task.modelAnswer.text} label="Play model sentence" />
            </div>
          )}
        </div>
      </article>
      {complete(state) && index < questions.length - 1 && <button className="primary-button next-button" type="button" onClick={() => setIndex(index + 1)}>Next sentence →</button>}
      {allComplete && <Completion label="Sentence Building" score={score} maximum={questions.length * 3} onGenerate={onGenerate} generating={generating} colour="building" />}
    </section>
  );
}

function ListenSpellRunner({ title, items, roundType, onComplete, onGenerate, generating }) {
  const questions = useMemo(() => roundType === 'ai' ? shuffle(items) : sample(items, 10), [items, roundType]);
  const [index, setIndex] = useState(0);
  const [states, setStates] = useState(() => questions.map(() => ({
    response: '', attempts: 0, passed: false, skipped: false, mark: false, feedback: '',
  })));
  const reported = useRef(false);
  const state = states[index];
  const item = questions[index];
  const score = states.reduce((sum, value) => sum + Number(value.mark), 0);
  const complete = (value) => value.passed || value.skipped;
  const allComplete = states.every(complete);

  useEffect(() => {
    if (!allComplete || reported.current) return;
    reported.current = true;
    onComplete({ score, maximum: questions.length, targetIds: questions.map((value) => value.targetId) });
  }, [allComplete, onComplete, questions, score]);

  function submit(event) {
    event.preventDefault();
    const accepted = item.answer.acceptedSpellings.map(normaliseAnswer);
    const correct = accepted.includes(normaliseAnswer(state.response));
    patchAt(setStates, index, {
      attempts: state.attempts + 1,
      passed: correct,
      mark: correct && state.attempts === 0,
      feedback: correct
        ? state.attempts === 0 ? '✅ Correct — 1 mark earned.' : '✅ Correct. Compare your spelling with the reviewed answer.'
        : '❌ Listen again and check every letter.',
    });
  }

  function passQuestion() {
    patchAt(setStates, index, {
      response: '',
      passed: false,
      skipped: true,
      mark: false,
      feedback: '',
    });
    if (index < questions.length - 1) setIndex(index + 1);
  }

  return (
    <section className="exercise-mode exercise-spell">
      <div className="exercise-mode-heading">
        <div><p className="eyebrow mode-spell-text">{roundType === 'ai' ? 'LISTEN & SPELL · AI ROUND' : '🎧 LISTEN & SPELL'}</p><h2>Complete the missing word</h2><p>Hear the whole sentence, then type the missing target. Ten items, one mark each.</p></div>
        <StepButtons current={index} states={states} isDone={complete} isSkipped={(value) => value.skipped} onSelect={setIndex} onGenerate={onGenerate} generating={generating} colour="spell" />
      </div>
      <article className="exercise-card spell-card">
        <div className="exercise-card-header"><div><span>SENTENCE {index + 1} OF {questions.length}</span><strong>Target selected from {title}</strong></div><span className="marks-pill">1 mark</span></div>
        <div className="exercise-card-body">
          <SpeechButton className="audio-prompt audio-banner-button" text={item.audio.speechText} label="Play the whole sentence">
            <strong>Hear the whole sentence</strong>
          </SpeechButton>
          <p className="question-sentence">{replaceBlank(item.prompt.sentenceWithBlank)}</p>
          {!state.skipped && <form className="response-form" onSubmit={submit}>
            <label>Type the missing word or name<input value={state.response} onChange={(event) => patchAt(setStates, index, { response: event.target.value, feedback: '' })} disabled={state.passed} /></label>
            {!state.passed && <div className="response-actions"><button className="primary-button spell-primary" type="submit" disabled={!state.response.trim()}>Check spelling</button><button className="pass-button" type="button" onClick={passQuestion}>Pass</button></div>}
          </form>}
          {state.skipped && <div className="passed-notice">Passed — this spelling was skipped and not reviewed.</div>}
          {state.feedback && <div className={`feedback ${state.passed ? 'feedback-correct' : 'feedback-incorrect'}`}>{state.feedback}</div>}
          {state.passed && <div className="model-answer"><strong>{item.answer.displayText}</strong><p>{item.audio.displayText}</p><p className="support-text">{item.prompt.sentenceZh}</p><SpeechButton text={item.audio.speechText} label="Play reviewed answer" /></div>}
        </div>
      </article>
      {complete(state) && index < questions.length - 1 && <button className="primary-button next-button" type="button" onClick={() => setIndex(index + 1)}>Next sentence →</button>}
      {allComplete && <Completion label="Listen & Spell" score={score} maximum={questions.length} onGenerate={onGenerate} generating={generating} colour="spell" />}
    </section>
  );
}

function ListenDetailsRunner({ title, items, roundType, onComplete, onGenerate, generating }) {
  const questions = useMemo(() => roundType === 'ai' ? shuffle(items) : sample(items, 5), [items, roundType]);
  const [index, setIndex] = useState(0);
  const [states, setStates] = useState(() => questions.map(() => ({
    response: '', feedback: '', passed: false, skipped: false, checking: false,
  })));
  const reported = useRef(false);
  const state = states[index];
  const item = questions[index];
  const score = states.filter((value) => value.passed).length * 3;
  const complete = (value) => value.passed || value.skipped;
  const allComplete = states.every(complete);

  useEffect(() => {
    if (!allComplete || reported.current) return;
    reported.current = true;
    onComplete({ score, maximum: questions.length * 3, targetIds: questions.map((value) => value.targetId) });
  }, [allComplete, onComplete, questions, score]);

  async function submit(event) {
    event.preventDefault();
    if (!state.response.trim()) return;
    const localAnswers = [...(item.answerCheck.acceptedFragments ?? []), ...(item.answerCheck.acceptedCompleteAnswers ?? [])].map(normaliseAnswer);
    if (localAnswers.includes(normaliseAnswer(state.response))) {
      patchAt(setStates, index, { passed: true, feedback: '✅ Accepted. You identified the crucial detail.' });
      return;
    }
    patchAt(setStates, index, { checking: true, feedback: '✨ Checking the meaning of your answer…' });
    try {
      const result = await checkResponse({
        mode: 'listen-for-details',
        theme_id: item.id.split('-').slice(0, 2).join('-'),
        item_id: item.id,
        audio_context: item.audio.displayText,
        question: item.question.text,
        crucial_information: item.answerCheck.crucialInformation,
        accepted_fragments: item.answerCheck.acceptedFragments,
        student_response: state.response,
      });
      patchAt(setStates, index, { checking: false, passed: result.pass === true, feedback: `${result.pass ? '✅' : '❌'} ${result.feedback}` });
    } catch (error) {
      patchAt(setStates, index, { checking: false, feedback: `⚠️ ${error.message}` });
    }
  }

  function passQuestion() {
    patchAt(setStates, index, {
      response: '',
      feedback: '',
      passed: false,
      skipped: true,
      checking: false,
    });
    if (index < questions.length - 1) setIndex(index + 1);
  }

  return (
    <section className="exercise-mode exercise-details">
      <div className="exercise-mode-heading">
        <div><p className="eyebrow mode-details-text">{roundType === 'ai' ? 'LISTEN FOR DETAILS · AI ROUND' : '🔎 LISTEN FOR DETAILS'}</p><h2>Find the key information</h2><p>Hear a short HKDSE-style monologue and answer with the crucial information.</p></div>
        <StepButtons current={index} states={states} isDone={complete} isSkipped={(value) => value.skipped} onSelect={setIndex} onGenerate={onGenerate} generating={generating} colour="details" />
      </div>
      <article className="exercise-card details-card">
        <div className="exercise-card-header"><div><span>QUESTION {index + 1} OF {questions.length}</span><strong>HKDSE Paper 3 Part A-style listening</strong></div><span className="marks-pill">Accepted = 3 marks</span></div>
        <div className="exercise-card-body">
          <SpeechButton className="audio-prompt audio-banner-button details-audio" text={item.audio.speechText} label="Play the monologue">
            <strong>Play monologue</strong>
          </SpeechButton>
          <div className="listening-question"><span>QUESTION</span><h3>{item.question.text}</h3><p lang="zh-Hant">{item.question.textZh}</p></div>
          {!state.skipped && <form className="response-form" onSubmit={submit}>
            <label>Give the key information or a complete sentence<input value={state.response} onChange={(event) => patchAt(setStates, index, { response: event.target.value, feedback: '' })} disabled={state.passed || state.checking} /></label>
            {!state.passed && <div className="response-actions"><button className="primary-button details-primary" type="submit" disabled={!state.response.trim() || state.checking}>{state.checking ? 'Checking…' : 'Check answer'}</button><button className="pass-button" type="button" onClick={passQuestion} disabled={state.checking}>Pass</button></div>}
          </form>}
          {state.skipped && <div className="passed-notice">Passed — this answer was skipped and not reviewed.</div>}
          {state.feedback && <div className={`feedback ${state.checking ? 'feedback-checking' : state.passed ? 'feedback-correct' : 'feedback-incorrect'}`}>{state.feedback}</div>}
          {state.passed && <div className="model-answer"><strong>Transcript and review</strong><p>{item.audio.displayText}</p><p className="support-text">{item.audio.reviewTextZh}</p><SpeechButton text={item.audio.speechText} label="Play reviewed monologue" /></div>}
        </div>
      </article>
      {complete(state) && index < questions.length - 1 && <button className="primary-button next-button" type="button" onClick={() => setIndex(index + 1)}>Next question →</button>}
      {allComplete && <Completion label="Listen for Details" score={score} maximum={questions.length * 3} onGenerate={onGenerate} generating={generating} colour="details" />}
    </section>
  );
}

export default function ExerciseRunner({
  mode,
  theme,
  subtopic,
  headword,
  content,
  round,
  onComplete,
  onGenerate,
  onPreparePassage,
  generating,
}) {
  const roundType = round?.type ?? 'stored';
  if (mode === 'guided-practice') {
    return (
      <GuidedRunner
        theme={theme}
        headword={headword}
        items={round?.items ?? headword.collocations}
        passage={round?.passage}
        passageStatus={round?.passageStatus ?? 'loading'}
        passageError={round?.passageError}
        roundType={roundType}
        onComplete={onComplete}
        onGenerate={onGenerate}
        onPreparePassage={onPreparePassage}
        generating={generating}
      />
    );
  }
  if (mode === 'sentence-building') {
    return <SentenceBuildingRunner headword={headword} items={round?.items ?? headword.collocations} roundType={roundType} onComplete={onComplete} onGenerate={onGenerate} generating={generating} />;
  }
  if (mode === 'listen-spell') {
    return <ListenSpellRunner title={subtopic.title} items={round?.items ?? content.roundSpecification.listenSpell.items} roundType={roundType} onComplete={onComplete} onGenerate={onGenerate} generating={generating} />;
  }
  return <ListenDetailsRunner title={subtopic.title} items={round?.items ?? content.roundSpecification.listenForDetails.items} roundType={roundType} onComplete={onComplete} onGenerate={onGenerate} generating={generating} />;
}
